import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PhoneOutlined,
  StarFilled,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DATE_TIME_FORMAT } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { camelToWords, formatDate, snakeToCamelCase, toTitleCase } from "common/utils";
import { AktDatePicker } from "components/aktDatePicker";
import { DisplayComponent, DisplayPhoneNumber } from "components/formItems";
import { useUserType } from "features/auth";
import { ClickToCallButton } from "features/internalDialer";
import {
  useCreateDebtorPhoneNumberMutation,
  useDeleteDebtorPhoneNumberMutation,
  useReplicatePacketedDebtorPhoneNumbersMutation,
  useUpdateDebtorPhoneNumberMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import moment from "moment-timezone";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useCompactMode } from "providers/compactModeProvider";
import {
  ConsentDot,
  ConsentItem,
  CONSENT_STATUSES,
  getConsentColor,
  getStatusTagColor,
  toConsentValue,
} from "./compactCardUtils";

const StyledCard = styled.div`
  margin-top: 0px;
  margin-left: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledHeaderText = styled.h4`
  margin: 2px 0;
`;

const StyledTag = styled(Tag)`
  margin-right: 0;
  font-weight: normal;
`;

const StyledCompactItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2px 0;
  min-width: 0;
`;

const StyledCompactSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const StyledCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .ant-btn-link {
    padding: 0;
    min-width: auto;
    height: auto;
    line-height: 1;
  }
`;

const StyledPrimaryGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const StyledCompactPrimary = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledStarFilled = styled(StarFilled)`
  color: #faad14;
  flex-shrink: 0;
`;

const StyledTrailingActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 4px;
  }
`;

const StyledLeadingIcon = styled(PhoneOutlined)`
  color: #8c8c8c;
  flex-shrink: 0;
`;

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: #1677ff;
  cursor: pointer;
`;

const StyledDescriptions = styled(Descriptions)`
  white-space: nowrap;
`;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export function DebtorPhoneInfoCard({
  triggerReplicatePacketedDebtorUpdatesModal = () => {},
  phone = undefined,
  isNew = undefined,
  onCreate = undefined,
  onCancel = undefined,
  title = undefined,
  isFirstItem = true,
}) {
  const { isAgencyUserType } = useUserType();
  const { isCompactMode } = useCompactMode();
  const { debtorId } = useParams();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(isNew || false);
  const { data: constants } = useFetchBackendConstantsQuery();
  const [updateDebtorPhoneNumber, { isLoading: isUpdateLoading }] =
    useUpdateDebtorPhoneNumberMutation();
  const [createDebtorPhoneNumber, { isLoading: isCreateLoading }] =
    useCreateDebtorPhoneNumberMutation();
  const [deleteDebtorPhoneNumber, { isLoading: isDeleteLoading }] =
    useDeleteDebtorPhoneNumberMutation();
  const [replicatePacketedDebtorPhoneNumbers] = useReplicatePacketedDebtorPhoneNumbersMutation();

  const makeOnConsentStatusChangeCallback = useCallback(
    (statusChangeDateFieldName) => {
      return (value) => {
        if (
          value === "consent" &&
          // The intention here isis: if the user touched the value manually
          // (excluding when using the clearing buttn)
          // we don't want to mess with user selected values.
          (!form.isFieldTouched(statusChangeDateFieldName) ||
            !form.getFieldValue(statusChangeDateFieldName))
        ) {
          form.setFields([
            // setFieldValue would set touched to true, use this to avoid that.
            {
              name: statusChangeDateFieldName,
              value: formatDate(moment().endOf("day")),
              touched: false,
            },
          ]);
        }
      };
    },
    [form],
  );

  const onPermissionToCallStatusChange = makeOnConsentStatusChangeCallback(
    "permissionToCallStatusChangeDate",
  );
  const onPermissionToTextStatusChange = makeOnConsentStatusChangeCallback(
    "permissionToTextStatusChangeDate",
  );
  const onAiPermissionToCallStatusChange = makeOnConsentStatusChangeCallback(
    "aiPermissionToCallStatusChangeDate",
  );

  const {
    data: {
      [PERMISSIONS.DEBTOR_PHONE_NUMBER__CREATE]: isAddDebtorPhoneNumberAuthorized,
      [PERMISSIONS.DEBTOR_PHONE_NUMBER__UPDATE]: isUpdateDebtorPhoneNumberAuthorized,
      [PERMISSIONS.DEBTOR_PHONE_NUMBER__DELETE]: isDeleteDebtorPhoneNumberAuthorized,
      [PERMISSIONS.PHONE__CALL_DEBTOR]: isCallDebtorAuthorized,
    },
  } = useAuthorizations();
  const editableDataSourceTypeValues = constants?.editableDataSourceTypes?.map(
    ({ value }) => value,
  );

  const deletePhoneNumber = async () => {
    const result = await deleteDebtorPhoneNumber({ debtorId, phoneNumberId: phone.id });
    if ("data" in result) {
      message.success("Phone number deleted successfully!");
    }
  };
  const toggleIsEditing = async () => {
    if (isEditing) {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return null; // Form will handle error messages
      }

      const action = isNew
        ? createDebtorPhoneNumber({ debtorId, ...values })
        : updateDebtorPhoneNumber({
            debtorId,
            phoneNumberId: phone.id,
            ...values,
          });
      const result = await action;

      if ("data" in result) {
        message.success("Saved successfully!");
        if (values.isPrimary) {
          triggerReplicatePacketedDebtorUpdatesModal({
            action: replicatePacketedDebtorPhoneNumbers,
            dataId: result.data.id,
            newData: result.data.number,
            type: "phone",
          });
        }
        if (isNew) onCreate();
      }

      if ("error" in result) {
        form.setFields(formatError(result.error));
        message.error("Could not update phone info");
        return;
      }
      setIsEditing(false);
    } else {
      setIsEditing(true);
      form.setFieldsValue(phone);
    }
  };

  const onPrimaryCancel = () => {
    setIsEditing(false);
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 20 },
      sm: { span: 13 },
    },
  };

  const phoneTypes = constants?.phoneTypes?.map(({ display, value }) => ({
    value,
    label: display,
  }));

  const displayPermissionToCallStatusChangeDate = (
    <DisplayComponent
      isEditing={isEditing}
      slice={phone}
      label="Phone Consent Change Date"
      name="permissionToCallStatusChangeDate"
      outputFormatter={formatDate}
    >
      <AktDatePicker />
    </DisplayComponent>
  );

  const displayPermissionToTextStatusChangeDate = (
    <DisplayComponent
      isEditing={isEditing}
      slice={phone}
      label="SMS Consent Change Date"
      name="permissionToTextStatusChangeDate"
      outputFormatter={formatDate}
    >
      <AktDatePicker />
    </DisplayComponent>
  );

  const displayAiPermissionToCallStatusChangeDate = (
    <DisplayComponent
      isEditing={isEditing}
      slice={phone}
      label="AI Phone Consent Change Date"
      name="aiPermissionToCallStatusChangeDate"
      outputFormatter={formatDate}
    >
      <AktDatePicker />
    </DisplayComponent>
  );

  const displayTransUnionScore = (
    <DisplayComponent
      isEditing={false}
      slice={phone}
      label="Trans Union Score"
      name="transUnionScore"
    />
  );

  const displayTloScore = (
    <DisplayComponent isEditing={false} slice={phone} label="TLO Score" name="tloScore" />
  );

  const displayNeustarScore = (
    <DisplayComponent isEditing={false} slice={phone} label="Neustar Score" name="neustarScore" />
  );

  return (
    <StyledCard>
      {(isFirstItem || isNew || isEditing || !isCompactMode) && (
        <StyledHeader>
          <StyledHeaderText>
            <Space>
              {isNew ? "New Phone Number" : title}
              {!isCompactMode && phone?.isPrimary && !isEditing && (
                <StyledTag color="gold">PRIMARY</StyledTag>
              )}
            </Space>
          </StyledHeaderText>
          {!isCompactMode &&
            !isNew &&
            !isEditing &&
            isAgencyUserType &&
            (isCallDebtorAuthorized ||
              isUpdateDebtorPhoneNumberAuthorized ||
              isDeleteDebtorPhoneNumberAuthorized) && (
              <Row>
                {isCallDebtorAuthorized && (
                  <ClickToCallButton
                    phoneNumber={phone?.number}
                    e164Number={phone?.e164Number}
                    debtorId={debtorId}
                    phoneId={phone?.id}
                  />
                )}
                {isUpdateDebtorPhoneNumberAuthorized && (
                  <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                )}
                {isDeleteDebtorPhoneNumberAuthorized && (
                  <Popconfirm
                    placement="topRight"
                    okText="Yes"
                    title="Are you sure you want to delete this phone number?"
                    onConfirm={deletePhoneNumber}
                  >
                    <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Row>
            )}
        </StyledHeader>
      )}
      {isEditing && (
        <>
          <StyledForm
            labelAlign="left"
            form={form}
            {...formItemLayout}
            initialValues={{
              dataSource: "",
              permissionToCallStatus: "unknown",
              permissionToTextStatus: "unknown",
              aiPermissionToCallStatus: "unknown",
              status: "",
            }}
          >
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Primary"
              name="isPrimary"
              valuePropName="checked"
              hidden={phone?.isPrimary}
            >
              <Checkbox />
            </DisplayComponent>
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Phone"
              name="number"
              placeholder="XXX-XXX-XXXX"
              rules={[{ required: true, message: "Please enter a valid phone" }]}
            />
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Type"
              name="type"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                popupMatchSelectWidth={false}
                options={phoneTypes}
              />
            </DisplayComponent>
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Consent For Phone"
              name="permissionToCallStatus"
              rules={[{ required: true, message: "Please select a consent status" }]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                popupMatchSelectWidth={false}
                onChange={onPermissionToCallStatusChange}
                options={constants?.contactPermissionStatuses?.map((each) => ({
                  label: each.display,
                  value: each.value,
                }))}
              />
            </DisplayComponent>
            {displayPermissionToCallStatusChangeDate}
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Consent For SMS"
              name="permissionToTextStatus"
              rules={[{ required: true, message: "Please select a consent status" }]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                popupMatchSelectWidth={false}
                onChange={onPermissionToTextStatusChange}
                options={constants?.contactPermissionStatuses?.map((each) => ({
                  label: each.display,
                  value: each.value,
                }))}
              />
            </DisplayComponent>
            {displayPermissionToTextStatusChangeDate}
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Consent For AI Phone"
              name="aiPermissionToCallStatus"
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                popupMatchSelectWidth={false}
                onChange={onAiPermissionToCallStatusChange}
                options={constants?.contactPermissionStatuses?.map((each) => ({
                  label: each.display,
                  value: each.value,
                }))}
              />
            </DisplayComponent>
            {displayAiPermissionToCallStatusChangeDate}
            <DisplayComponent isEditing={isEditing} slice={phone} label="Status" name="status">
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                popupMatchSelectWidth={false}
                options={constants?.phoneStatuses?.map((each) => ({
                  label: each.display,
                  value: each.value,
                }))}
              />
            </DisplayComponent>
            <DisplayComponent isEditing={isEditing} slice={phone} label="Notes" name="notes">
              <Input.TextArea placeholder="Add Notes" />
            </DisplayComponent>
            {/* Only allow setting of data source if no data source is set, or it is editable (i.e. present in the editableDataSourceTypes) */}
            {(!phone?.dataSource || editableDataSourceTypeValues.includes(phone?.dataSource)) && (
              <DisplayComponent
                isEditing={isEditing}
                slice={phone}
                label="Data Source"
                name="dataSource"
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.editableDataSourceTypes?.map(({ display, value }) => ({
                    value,
                    label: display,
                  }))}
                />
              </DisplayComponent>
            )}
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="Is Right Party of Contact"
              name="isRightPartyOfContact"
            >
              <Select
                allowClear
                popupMatchSelectWidth={false}
                options={[
                  {
                    label: "Yes",
                    value: true,
                  },
                  {
                    label: "No",
                    value: false,
                  },
                  {
                    label: "None",
                    value: null,
                  },
                ]}
              />
            </DisplayComponent>
            <DisplayComponent
              isEditing={isEditing}
              slice={phone}
              label="First Contacted Right Party of Contact Time"
              name="firstContactedRightPartyOfContactTime"
              readOnly
              outputFormatter={(value) => moment(value).format(DATE_TIME_FORMAT)}
            />
          </StyledForm>
          <StyledButtonContainer>
            <Space>
              <Button onClick={isNew ? onCancel : onPrimaryCancel}>Cancel</Button>
              {(isAddDebtorPhoneNumberAuthorized || isUpdateDebtorPhoneNumberAuthorized) && (
                <Button
                  loading={isCreateLoading || isUpdateLoading}
                  onClick={toggleIsEditing}
                  type="primary"
                >
                  Save
                </Button>
              )}
            </Space>
          </StyledButtonContainer>
        </>
      )}
      {!isEditing && isCompactMode && (
        <StyledCompactItem>
          <StyledCompactRow>
            <StyledLeadingIcon />
            <StyledPrimaryGroup>
              <Tooltip
                title={`${phone.number}${
                  phone.type ? ` (${toTitleCase({ str: phone.type, delimiter: "_" })})` : ""
                }`}
              >
                <StyledCompactPrimary>
                  {phone.number}
                  {phone.type && ` (${toTitleCase({ str: phone.type, delimiter: "_" })})`}
                </StyledCompactPrimary>
              </Tooltip>
              {phone.isPrimary && (
                <Tooltip title="Primary">
                  <StyledStarFilled />
                </Tooltip>
              )}
            </StyledPrimaryGroup>
            {phone.status && (
              <StyledTag color={getStatusTagColor(phone.status)}>
                {camelToWords(snakeToCamelCase(phone.status))}
              </StyledTag>
            )}
            <StyledTrailingActions>
              {isCallDebtorAuthorized && (
                <ClickToCallButton
                  phoneNumber={phone?.number}
                  e164Number={phone?.e164Number}
                  debtorId={debtorId}
                  phoneId={phone?.id}
                />
              )}
              <Popover
                trigger="hover"
                placement="top"
                content={
                  <StyledDescriptions size="small" column={1} bordered>
                    {phone.type ? (
                      <Descriptions.Item label="Type">
                        {toTitleCase({ str: phone.type, delimiter: "_" })}
                      </Descriptions.Item>
                    ) : null}
                    {phone.isPrimary ? (
                      <Descriptions.Item label="Primary">Yes</Descriptions.Item>
                    ) : null}
                    {phone.status ? (
                      <Descriptions.Item label="Status">
                        {camelToWords(snakeToCamelCase(phone.status))}
                      </Descriptions.Item>
                    ) : null}
                    {CONSENT_STATUSES.includes(phone.permissionToCallStatus) ? (
                      <Descriptions.Item label="Phone Consent">
                        {camelToWords(snakeToCamelCase(phone.permissionToCallStatus))}
                        {phone.permissionToCallStatusChangeDate &&
                          ` (${formatDate(phone.permissionToCallStatusChangeDate)})`}
                      </Descriptions.Item>
                    ) : null}
                    {CONSENT_STATUSES.includes(phone.permissionToTextStatus) ? (
                      <Descriptions.Item label="SMS Consent">
                        {camelToWords(snakeToCamelCase(phone.permissionToTextStatus))}
                        {phone.permissionToTextStatusChangeDate &&
                          ` (${formatDate(phone.permissionToTextStatusChangeDate)})`}
                      </Descriptions.Item>
                    ) : null}
                    {CONSENT_STATUSES.includes(phone.aiPermissionToCallStatus) ? (
                      <Descriptions.Item label="AI Phone Consent">
                        {camelToWords(snakeToCamelCase(phone.aiPermissionToCallStatus))}
                        {phone.aiPermissionToCallStatusChangeDate &&
                          ` (${formatDate(phone.aiPermissionToCallStatusChangeDate)})`}
                      </Descriptions.Item>
                    ) : null}
                    {phone.isRightPartyOfContact !== undefined ? (
                      <Descriptions.Item label="Right Party Contact">
                        {{ true: "Yes", false: "No" }[phone.isRightPartyOfContact] ?? "Unknown"}
                      </Descriptions.Item>
                    ) : null}
                    {phone.firstContactedRightPartyOfContactTime ? (
                      <Descriptions.Item label="First Right Party Contact Time">
                        {moment(phone.firstContactedRightPartyOfContactTime).format(
                          DATE_TIME_FORMAT,
                        )}
                      </Descriptions.Item>
                    ) : null}
                    {phone.transUnionScore != null ? (
                      <Descriptions.Item label="Trans Union">
                        {phone.transUnionScore}
                      </Descriptions.Item>
                    ) : null}
                    {phone.tloScore != null ? (
                      <Descriptions.Item label="TLO Score">{phone.tloScore}</Descriptions.Item>
                    ) : null}
                    {phone.neustarScore != null ? (
                      <Descriptions.Item label="Neustar">{phone.neustarScore}</Descriptions.Item>
                    ) : null}
                    {phone.dataSource ? (
                      <Descriptions.Item label="Data Source">
                        {camelToWords(snakeToCamelCase(phone.dataSource))}
                      </Descriptions.Item>
                    ) : null}
                    {phone.createdAt ? (
                      <Descriptions.Item label="Created At">
                        {moment(phone.createdAt).format(DATE_TIME_FORMAT)}
                      </Descriptions.Item>
                    ) : null}
                    {phone.notes ? (
                      <Descriptions.Item label="Notes">{phone.notes}</Descriptions.Item>
                    ) : null}
                  </StyledDescriptions>
                }
              >
                <StyledInfoIcon />
              </Popover>
              {isAgencyUserType &&
                (isUpdateDebtorPhoneNumberAuthorized || isDeleteDebtorPhoneNumberAuthorized) && (
                  <>
                    {isUpdateDebtorPhoneNumberAuthorized && (
                      <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                    )}
                    {isDeleteDebtorPhoneNumberAuthorized && (
                      <Popconfirm
                        placement="topRight"
                        okText="Yes"
                        title="Are you sure you want to delete this phone number?"
                        onConfirm={deletePhoneNumber}
                      >
                        <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </>
                )}
            </StyledTrailingActions>
          </StyledCompactRow>
          {(CONSENT_STATUSES.includes(phone?.permissionToCallStatus) ||
            CONSENT_STATUSES.includes(phone?.permissionToTextStatus) ||
            CONSENT_STATUSES.includes(phone?.aiPermissionToCallStatus) ||
            phone?.isRightPartyOfContact !== undefined) && (
            <StyledCompactSubRow>
              {(() => {
                const items = [
                  { status: phone?.permissionToCallStatus, label: "Phone" },
                  { status: phone?.permissionToTextStatus, label: "SMS" },
                  { status: phone?.aiPermissionToCallStatus, label: "AI Phone" },
                ].filter(({ status }) => CONSENT_STATUSES.includes(status));
                return items.map(({ status, label }) => (
                  <Tooltip
                    key={label}
                    title={`${label} Consent: ${camelToWords(snakeToCamelCase(status))}`}
                  >
                    <ConsentItem>
                      <ConsentDot $color={getConsentColor(toConsentValue(status))} />
                      {label}
                    </ConsentItem>
                  </Tooltip>
                ));
              })()}
              {phone?.isRightPartyOfContact !== undefined && (
                <Tooltip
                  title={`Right Party Contact: ${
                    { true: "Yes", false: "No" }[phone.isRightPartyOfContact] ?? "Unknown"
                  }`}
                >
                  <ConsentItem>
                    <ConsentDot $color={getConsentColor(phone.isRightPartyOfContact)} />
                    RPC
                  </ConsentItem>
                </Tooltip>
              )}
            </StyledCompactSubRow>
          )}
        </StyledCompactItem>
      )}
      {!isEditing && !isCompactMode && (
        <StyledDiv>
          <DisplayPhoneNumber
            notes={phone.notes}
            isValid={phone.type !== "invalid"}
            number={phone.number}
            type={toTitleCase({ str: phone.type, delimiter: "_" })}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={phone}
            label="Status"
            name="status"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
          />
          <DisplayComponent
            isEditing={false}
            slice={phone}
            label="Data Source"
            name="dataSource"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
          />
          <DisplayComponent
            isEditing={false}
            slice={phone}
            label="Created At"
            name="createdAt"
            outputFormatter={(value) => moment(value).format(DATE_TIME_FORMAT)}
          />
          {displayPermissionToCallStatusChangeDate}
          {displayPermissionToTextStatusChangeDate}
          {displayAiPermissionToCallStatusChangeDate}
          {displayTransUnionScore}
          {displayTloScore}
          {displayNeustarScore}
        </StyledDiv>
      )}
    </StyledCard>
  );
}
